import { resolveStockHeroImageUrl } from "../../constants/landing-images";
import type { HeroImageMode, ThemeVariant } from "../../../types/business";
import type { ProjectImage } from "./types";

type HeroSourceOptions = {
  tradeDisplayName: string;
  heroImageMode?: HeroImageMode;
  heroProjectImageId?: number | null;
  /** ActiveStorage homepage banner; takes precedence over gallery-based hero. */
  heroBannerUrl?: string | null;
  projectImages?: ProjectImage[];
};

const THEME_CLASS_MAP: Record<ThemeVariant, string> = {
  default: "site-theme-default",
  modern: "site-theme-modern",
  bold: "site-theme-bold",
};

export const resolveSiteThemeClassName = (
  themeVariant?: ThemeVariant,
): string => {
  const variant = themeVariant ?? "default";
  return THEME_CLASS_MAP[variant] || THEME_CLASS_MAP.default;
};

/** When design tokens are saved, avoid site-theme-modern/bold palette clashes with injected CSS variables. */
export const resolveContractorSiteThemeClass = (
  themeVariant: ThemeVariant | undefined,
  hasPublishedDesignTokens: boolean | undefined,
): string => {
  if (hasPublishedDesignTokens) {
    return THEME_CLASS_MAP.default;
  }
  return resolveSiteThemeClassName(themeVariant);
};

export const resolveHeroImageSource = ({
  tradeDisplayName,
  heroImageMode = "stock",
  heroProjectImageId,
  heroBannerUrl,
  projectImages = [],
}: HeroSourceOptions): string => {
  if (heroBannerUrl?.trim()) {
    return heroBannerUrl.trim();
  }

  if (heroProjectImageId != null && projectImages.length > 0) {
    const heroId = Number(heroProjectImageId);
    const chosen = projectImages.find((img) => Number(img.id) === heroId);
    if (chosen) {
      return chosen.largeUrl || chosen.imageUrl;
    }
  }

  if (heroImageMode === "first_project_image") {
    const firstProjectImage = projectImages[0];
    if (firstProjectImage) {
      return firstProjectImage.largeUrl || firstProjectImage.imageUrl;
    }
  }

  return resolveStockHeroImageUrl(tradeDisplayName);
};
